import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { Truck, Loader2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import {
  FormField,
  FormItem,
  FormMessage,
} from '@/components/molecules/form-field';
import { getCurrencySymbol } from '@/lib/constants/form-options';
import type { CreateShopFormData, UpdateShopFormData } from '../types/shop-form.types';

interface ShippingRate {
  refId: string;
  name: string;
  description?: string;
  price: string | number;
  currency: string;
  estimatedDeliveryDays?: number | null;
  deliveryType?: string;
}

interface ShopShippingRatesProps<T extends CreateShopFormData | UpdateShopFormData> {
  form: UseFormReturn<T>;
  shippingRates: ShippingRate[];
  isLoading?: boolean;
}

export function ShopShippingRates<T extends CreateShopFormData | UpdateShopFormData>({
  form,
  shippingRates,
  isLoading = false,
}: ShopShippingRatesProps<T>) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="size-5" />
          {t('shops.shippingRates')} *
        </CardTitle>
        <CardDescription>{t('shops.shippingRatesDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <FormField
          control={form.control}
          name={'shippingRates' as any}
          render={({ field }) => (
            <FormItem>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : shippingRates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Truck className="size-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">{t('shops.noShippingRates')}</p>
                  <p className="text-sm mt-1">{t('shops.createShippingRatesFirst')}</p>
                  <Button type="button" variant="outline" className="mt-4" asChild>
                    <Link to="/shipping">{t('shops.goToShippingRates')}</Link>
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {shippingRates.map((rate) => {
                    const isSelected = field.value.includes(rate.refId);
                    return (
                      <div
                        key={rate.refId}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => {
                          const newValue = isSelected
                            ? field.value.filter((id: string) => id !== rate.refId)
                            : [...field.value, rate.refId];
                          field.onChange(newValue);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            const newValue = isSelected
                              ? field.value.filter((id: string) => id !== rate.refId)
                              : [...field.value, rate.refId];
                            field.onChange(newValue);
                          }
                        }}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors select-none ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-muted-foreground/50'
                        }`}
                      >
                        <div className={`size-4 shrink-0 mt-0.5 rounded border flex items-center justify-center ${
                          isSelected
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-input'
                        }`}>
                          {isSelected && <Check className="size-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{rate.name}</span>
                            {rate.deliveryType && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-muted">
                                {t(`shops.shippingTypes.${rate.deliveryType}`)}
                              </span>
                            )}
                          </div>
                          {rate.description && (
                            <p className="text-sm text-muted-foreground mt-0.5 truncate">
                              {rate.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-1 text-sm">
                            <span className="font-medium text-primary">
                              {rate.price} {getCurrencySymbol(rate.currency)}
                            </span>
                            {rate.estimatedDeliveryDays && (
                              <span className="text-muted-foreground">
                                {rate.estimatedDeliveryDays} {t('shops.daysUnit')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}
